import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

// reducers
import { setCurrentAuthorUnitId } from 'src/reducers/authReducer';

// components
import UnitBadge from '../../components/UnitBadge';
import { UnitDropDownSelector, UnitSelectorContainer } from '../../components/SelectUnit/SelectUnit';
import Notification from 'src/components/NotificationManager/NotificationManager';
import StoryForm from '../../components/Create/Form/StoryForm';

// styles
import { PageTitle, FloatRight, TitleRow, Line } from '../../utils/appCSS/appStyles';

// utilities, constants
import { addPageAnalytics, CATEGORY_TYPE_CREATE } from 'src/utils/analytics';
import { pageNames, MESSAGE_INFO, MESSAGE_MIN_WIDTH, MESSAGE_MAX_WIDTH } from 'src/utils/appConstants/appConstants';

import { useUserPermissions } from 'src/hooks/useUserPermissions';
import { withPage } from 'src/contexts/PageContext';
import { selectUnits } from 'src/reducers/unitsReducer';

const { homePageName, createPageName } = pageNames;

const MultiUnitNotificationWrapper = styled.div`
  position: fixed;
  margin-top: 96px;
  min-width: ${MESSAGE_MIN_WIDTH};
  max-width: ${MESSAGE_MAX_WIDTH};
  z-index: 2;
  text-align: left;
`;

const CreateStoryPage = () => {
  const dispatch = useDispatch();
  const { user, currentAuthorUnit, currentAuthorUnitId } = useUserPermissions();
  const { t, i18n } = useTranslation('strings');
  const allUnits = useSelector(selectUnits);
  const units = allUnits?.filter(unit => unit.isApprover);

  React.useEffect(() => {
    const addAnalytics = () => {
      addPageAnalytics({
        infoName: createPageName, // always use English
        activePage: createPageName, // used to set primary category
        breadCrumbs: [homePageName, createPageName],
        language: i18n.language, // app language
        categoryType: CATEGORY_TYPE_CREATE,
        userInfo: user,
      });
    }

    addAnalytics();
  }, []);

  const handleUnitChange = async (newCurrentAuthorUnitId: string) => {
    dispatch(setCurrentAuthorUnitId({ currentAuthorUnitId: newCurrentAuthorUnitId }));
  };

  return (
    <>
      <div style={{ paddingTop: '2rem' }}>
        <UnitBadge unitType={currentAuthorUnit?.type}></UnitBadge>
      </div>
      <TitleRow>
        <PageTitle paddingTop="0px">{t('createStoryLabel')}</PageTitle>
        <UnitSelectorContainer>
          <div>
            {units?.length > 1 &&
              <React.Fragment>
                <FloatRight>
                  <UnitDropDownSelector
                    units={units}
                    activeUnitId={currentAuthorUnitId}
                    changeHandler={async (ev) => await handleUnitChange(ev.target.value)}
                    showId
                    showHelp
                  />
                </FloatRight>
                {/* multi-unit notification */}
                <MultiUnitNotificationWrapper id="multi-unit-notification-wrapper">
                  <Notification
                    notification={{
                      title: t('chooseUnitLabel'),
                      message: t('chooseUnitMessage'),
                      messageType: MESSAGE_INFO,
                    }}
                  />
                </MultiUnitNotificationWrapper>
              </React.Fragment>
            }
            {units?.length === 1 &&
              `${currentAuthorUnit?.localName} (${currentAuthorUnit?.unitNumber})`
            }
          </div>
        </UnitSelectorContainer>
      </TitleRow>

      <Line />

      <StoryForm
        isCreatePage
        prevPageName={createPageName}
      />
    </>
  );
};

export default withPage({ pageName: createPageName, pageTitle: 'createStoryPageTitle' })(CreateStoryPage);
